import {
  CascaderField,
  ContentItem,
  ContentItems,
  ContentWrapper,
  FieldLabel,
  Form,
  type OperationType,
  SelectField,
  TextAreaInputField,
  TextInputField,
} from '@/components';
import { SelectMinistryField } from '@/features/master-table/components';
import { UserSelectField } from '@/features/tenant-manager/user/_components/UserSelectField';
import {
  ProjectCategoryLabelModifier,
  projectCategoryEnum,
} from '@/utils/enum';
import type { FC } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';
import { useSelectFiledFormData } from '../../_hooks/project-form-handler';
import type { SsFormSchema } from '../../_model/project-form';
import { handleProjectCategoryChange } from '../../_utils/project-form-handler';
import { FiscalYearField } from '../fields/FiscalYearField';
import { LegalBasesArrayField } from '../fields/LegalBasesArrayField';
import { MainCostField } from '../fields/MainCostField';
import { OtherResponsibleOrganizationsArrayField } from '../fields/OtherResponsibleOrganizationsArrayField';
import { PeriodAndStartEndCheckField } from '../fields/PeriodAndStartEndCheckField';
import { PlansArrayField } from '../fields/PlansArrayField';
import { RelatedOrganizationArrayField } from '../fields/RelatedOrganizationArrayField';
import { SnapshotField } from '../fields/SnapshotField';
import { SortOrderField } from '../fields/SortOrderField';
import { RelatedProjectArrayField } from '../fields/related-project-array-field/RelatedProjectArrayField';

type FormValues = z.infer<typeof SsFormSchema>;
type InitialFormValues = {
  data: Partial<FormValues['data']>;
};

type Props = {
  operation: OperationType;
  ignoredIdsForRelatedProject?: string[];
};

export const SsProjectForm: FC<Props> = ({
  operation,
  ignoredIdsForRelatedProject,
}) => {
  const {
    control,
    formState: { errors },
    watch,
    resetField,
    setValue,
  } = useFormContext<InitialFormValues, unknown, FormValues>();
  const { t } = useTranslation();

  const [fiscalYear, fiscalYearOfSnapshot] = watch([
    'data.fiscalYear',
    'data.fiscalYearOfSnapshot',
  ]);

  const snapshotId = fiscalYearOfSnapshot?.at(-1);

  const { organizationOptions } = useSelectFiledFormData({
    inputFiscalYear: fiscalYear,
    inputSnapshotId: snapshotId,
  });

  return (
    <>
      <ContentWrapper paddingTop={0}>
        <ContentItem borderBottom="0" paddingBottom={0} paddingTop={0}>
          <ContentItems
            items={[
              <FiscalYearField
                key="data.fiscalYear"
                operation={operation}
                onChange={() => {
                  resetField('data.organizationIds', { defaultValue: [] });
                  resetField('data.mainCostIds', { defaultValue: [] });
                }}
              />,
              <SortOrderField
                key="data.sortOrder"
                hint={t('hint-project-order-number')}
              />,
              <SnapshotField key="data.snapshotId" />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('organization')}>
        <ContentItem>
          <ContentItems
            items={[
              <SelectMinistryField
                key="data.ministryId"
                name="data.ministryId"
                control={control}
                errors={errors}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem>
          <ContentItems
            items={[
              <CascaderField
                key="data.organizationIds"
                name="data.organizationIds"
                label={t('organization-in-charge')}
                hint={t('hint-organization-in-charge-for-ss')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                enableIntermediate
                disabled={!fiscalYear}
                required
                loading={organizationOptions.isPending}
                textNotInOption={t('not-exist-in-organization-options')}
              />,
            ]}
          />

          <ContentItems
            items={[
              <UserSelectField
                key="data.peopleInChargeIds"
                name="data.peopleInChargeIds"
                label={t('people-in-charge')}
                hint={t('hint-people-in-charge-for-ss')}
                control={control}
                errors={errors}
                mode="multiple"
                organizationFilter="modal"
                useNotInOption
                snapshotId={snapshotId ?? undefined}
                required
              />,
              <UserSelectField
                key="data.businessManagerIds"
                mode="multiple"
                name="data.businessManagerIds"
                label={t('business-manager')}
                hint={t('hint-business-manager-for-ss')}
                placeholder={t('placeholder-business-manager')}
                control={control}
                errors={errors}
                organizationFilter="modal"
                useNotInOption
                snapshotId={snapshotId ?? undefined}
                required
              />,
            ]}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <OtherResponsibleOrganizationsArrayField type="SS" />
          <RelatedOrganizationArrayField type="SS" />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('basic-information')}>
        <ContentItem>
          <ContentItems
            items={[
              <TextInputField
                key="data.name"
                name="data.name"
                autocompleteKey="ssProject.name"
                label={t('segment-name')}
                control={control}
                errors={errors}
                required
              />,
            ]}
          />

          <ContentItems
            mode="fillRight"
            items={[
              <SelectField
                key="data.category"
                name="data.category"
                label={t('project-category')}
                hint={t('hint-project-category-for-ss')}
                control={control}
                errors={errors}
                options={projectCategoryEnum.options.map((value) => ({
                  value,
                  label: ProjectCategoryLabelModifier[value],
                }))}
                required
                shouldCoexistOnChange
                onChange={(value) => {
                  handleProjectCategoryChange(
                    value,
                    fiscalYear,
                    setValue,
                    resetField,
                  );
                }}
              />,
            ]}
          />

          <ContentItems
            mode="fullLine"
            items={[
              <PeriodAndStartEndCheckField
                key="data.period-data.isUnknownStart-data.isNoScheduledEnd"
                hint={t('hint-project-start-end-year-for-ss')}
              />,
            ]}
          />
        </ContentItem>

        <ContentItem>
          <ContentItems
            items={[
              <MainCostField
                key="data.mainCostIds"
                fiscalYear={fiscalYear?.year().toString()}
              />,
              <CascaderField
                key="data.independentAdministrativeAgencyIds"
                name="data.independentAdministrativeAgencyIds"
                label={t('independent-administrative-agency')}
                control={control}
                errors={errors}
                options={organizationOptions.data}
                required
                loading={organizationOptions.isPending}
              />,
            ]}
          />

          <RelatedProjectArrayField
            operation={operation}
            type="SS"
            ignoredProjectIds={ignoredIdsForRelatedProject}
          />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TextAreaInputField
                key="data.segmentUnitConcept"
                name="data.segmentUnitConcept"
                label={t('segment-unit-concept')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('overview-and-purpose')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TextAreaInputField
                key="data.purpose"
                name="data.purpose"
                label={t('project-purpose')}
                hint={t('hint-project-purpose')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <TextAreaInputField
                key="data.task"
                name="data.task"
                label={t('project-task')}
                hint={t('hint-project-task-for-ss')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
            ]}
          />

          <ContentItems
            items={[
              <TextAreaInputField
                key="data.overview"
                name="data.overview"
                label={t('project-overview')}
                hint={t('hint-project-overview-for-ss')}
                control={control}
                errors={errors}
                rows={3}
                required
              />,
              <TextInputField
                key="data.overviewUrl"
                name="data.overviewUrl"
                label={t('overview-url')}
                hint={t('hint-overview-url-for-ss')}
                control={control}
                errors={errors}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('legal-bases')}>
        <ContentItem>
          <LegalBasesArrayField />
        </ContentItem>

        <ContentItem borderBottom={0} paddingBottom={0}>
          <Form.Item
            label={
              <FieldLabel
                text={t('project-plan')}
                hint={t('hint-related-plans')}
              />
            }
            key="project-plan"
          >
            <PlansArrayField autocompleteKey="ssProject.plan.url" />
          </Form.Item>
        </ContentItem>
      </ContentWrapper>

      <ContentWrapper title={t('others')}>
        <ContentItem borderBottom={0} paddingBottom={0}>
          <ContentItems
            items={[
              <TextInputField
                key="data.oldProjectNumber"
                name="data.oldProjectNumber"
                label={t('old-project-number')}
                hint={t('hint-old-project-number-for-ss')}
                control={control}
                errors={errors}
              />,
              <TextAreaInputField
                key="data.remark"
                name="data.remark"
                label={t('remark')}
                control={control}
                errors={errors}
                rows={3}
              />,
            ]}
          />
        </ContentItem>
      </ContentWrapper>
    </>
  );
};
